import { ContextType, consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { pageStyles } from './components/cssstyles.js';
import { contexts } from './contexts.js';
import { platformUrl } from './platformUrl.js';
import { resetcss } from './resetcss.js';
import { type RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-login')
export class PageLogin extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [ '/sign-in' ],
      render: () => html`<page-login></page-login>`,
    }];
  }

  static page() {
    return html`<page-login></page-login>`;
  }

  @property() redirect?: string;
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  private _returnTo(): string {
    const queryReturnTo = new URLSearchParams(window.location.search).get('return_to');
    if (this.redirect || queryReturnTo)
      return this.redirect ?? queryReturnTo!;
    return window.location.pathname === '/sign-in' ? platformUrl('/') : window.location.href;
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Sign In' }),
  });

  override render() {
    const returnTo = encodeURIComponent(this._returnTo());
    const hasProvider = this._serverInfo?.enabledGithub || this._serverInfo?.enabledGitlab;
    return html`
      <app-header .customTitle=${""}></app-header>
      <app-body-card>
        <div class="title">
          <img src="/logo.svg" class="logo">
          <span>Sign in to flakiness.io</span>
        </div>
        <div class=info>Use one of your connected accounts to access your projects and reports.</div>
        ${this._serverInfo ? html`
          <div class=providers>
            ${this._serverInfo.enabledGithub ? html`
              <sl-button class=provider size=large href=${`/login/github?return_to=${returnTo}`}>
                <sl-icon slot=prefix name=github></sl-icon>Sign in with GitHub
              </sl-button>
            ` : nothing}
            ${this._serverInfo.enabledGitlab ? html`
              <sl-button class="provider gitlab" size=large href=${`/login/gitlab?return_to=${returnTo}`}>
                <sl-icon slot=prefix name=gitlab></sl-icon>Sign in with GitLab
              </sl-button>
            ` : nothing}
          </div>
          ${hasProvider ? nothing : html`<div class=unavailable>No sign-in provider is configured.</div>`}
        ` : html`<sl-spinner></sl-spinner>`}
      </app-body-card>
      <app-footer></app-footer>
    `
  }

  static styles = [resetcss, pageStyles, css`
    :host {
      background-color: var(--fk-color-canvas);
    }

    h-box {
      align-items: center;
      justify-content: center;
    }

    .info {
      color: var(--sl-color-neutral-500);
      max-width: 24rem;
      text-align: center;
    }

    .providers {
      display: grid;
      gap: var(--sl-spacing-small);
      width: min(20rem, 100%);
    }

    .provider {
      width: 100%;

      &::part(base) {
        justify-content: flex-start;
      }

      sl-icon {
        font-size: 1.2em;
      }

      &.gitlab sl-icon {
        color: #e24329;
      }
    }

    .unavailable {
      color: var(--sl-color-danger-600);
    }

    .title {
      font-size: var(--sl-font-size-x-large);
      font-family: "Raleway", sans-serif;
      font-optical-sizing: auto;
      font-weight: 400;
      display: flex;
      align-items: center;
      justify-content: center;
      font-style: normal;
      gap: 10px;

      .logo {
        width: 1.25em;
      }
    }

  `];
}
